import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Body, Button, Heading, I, InfoCallout, Stack } from '@leanin/ui';
import { FlowShell, type FlowStep } from '@/components/onboarding/FlowShell';
import { LoopVideo } from '@/components/media/LoopVideo';
import welcomeMp4 from '@/assets/onboarding/welcome.mp4';
import welcomeWebm from '@/assets/onboarding/welcome.webm';
import welcomePoster from '@/assets/onboarding/welcome-poster.png?w=440;880;1320&format=avif;webp;png&as=picture';

const STEPS: ReadonlyArray<FlowStep> = [
  { label: 'Welcome' },
  { label: 'Lean In Circles' },
  { label: 'Get involved' },
  { label: 'Build leadership skills' },
  { label: "You're in" },
];

export const Route = createFileRoute('/onboarding/welcome')({
  component: OnboardingWelcomePage,
});

function OnboardingWelcomePage() {
  const navigate = useNavigate();
  return (
    <FlowShell
      role="Lean In Member"
      stepIndex={0}
      steps={STEPS}
      minutes={2}
      title="Welcome to the Lean In community — we are SO glad you’re here."
      primary={
        <Button
          variant="primary"
          icon={I.arrow}
          onClick={() => navigate({ to: '/onboarding/circles' })}
        >
          Get started
        </Button>
      }
    >
      <Stack gap="lg">
        <LoopVideo
          webm={welcomeWebm}
          mp4={welcomeMp4}
          poster={welcomePoster}
          aria-label="Welcome to the Lean In community"
          style={{ width: '100%', height: 'auto', display: 'block' }}
        />

        <Stack gap="md">
          <Heading level={2} style={{ fontStyle: 'italic' }}>
            Dear Community Member,
          </Heading>
          <Body size="md">
            This community exists to help women learn from one another,
            support one another, and grow together. Whether you’re looking
            for connection, leadership growth, honest conversation, or
            encouragement through life and work — you belong here.
          </Body>
          <Body size="md">
            This is a place to ask questions, celebrate wins, navigate
            challenges, and connect with people who genuinely want to see
            you succeed.
          </Body>
          <Body size="md">
            We hope you’ll join the conversation, share your perspective,
            and support others along the way. Chances are, someone here
            will do the same for you.
          </Body>
          <Body size="md">
            Thank you for being part of this community. We can’t wait to
            see what you bring to it.
          </Body>
          <Body size="md">— The Lean In Team</Body>
        </Stack>

        <InfoCallout tone="lilac" pad="default">
          Next up we’ll take you on a quick tour of what you can do here.
        </InfoCallout>
      </Stack>
    </FlowShell>
  );
}
